import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
  listFornitori,
  getFornitoreCompleto,
  updateFornitore,
  duplicateFornitore,
} from "../Admin/adminApi";
import "./AdminFornitori.css";
import CityField from "../components/CityField";
import SupplierImageCropper from "../components/SupplierImageCropper";
import { SUPPLIER_IMAGE_CSS_RATIO } from "../utils/supplierImage";

export default function AdminFornitori() {
  const tkn = localStorage.getItem("token");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [photo, setPhoto] = useState(null);
  const [coverUrl, setCoverUrl] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [originalUrl, setOriginalUrl] = useState("");
  const [cropping, setCropping] = useState(false);
  const [crop, setCrop] = useState(null);

  // AdminRoute checks the authenticated role via /me before mounting this page.
  useEffect(() => {
    if (!photo) { setPreviewUrl(""); return; }
    const url = URL.createObjectURL(photo);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  // sort
  const [sortBy, setSortBy] = useState("nome"); // nome | alias | telefono | email | citta
  const [sortDir, setSortDir] = useState("asc");

  // modale anagrafica
  const [openId, setOpenId] = useState(null);
  const [edit, setEdit] = useState({
    nome: "",
    cognome: "",
    alias: "",
    telefono: "",
    email: "",
    indirizzo: "",
    citta: "",
    cap: "",
    provincia: "",
    codice_fiscale: "",
    partita_iva: "",
  });
  const isOpen = openId != null;

  // dialog duplica
  const [dupId, setDupId] = useState(null);
  const [dupNome, setDupNome] = useState("");
  const [dupSaving, setDupSaving] = useState(false);
  const isDupOpen = dupId != null;

  const norm = (r = {}) => ({
    id: r.id ?? r.fornitore_id ?? r.supplier_id ?? r.ID,
    nome: r.name ?? r.ragione_sociale ?? "",
    cognome: r.cognome ?? r.last_name ?? r.surname ?? "",
    alias: r.nome ?? r.slug ?? r.short_name ?? "",
    telefono: r.telefono ?? r.phone ?? "",
    email: r.email ?? "",
    citta: r.citta ?? r.city ?? "",
    provincia: r.provincia ?? r.province ?? r.pr ?? "",
  });

  const load = async () => {
    try {
      setLoading(true);
      const data = await listFornitori(tkn);
      const arr = (Array.isArray(data) ? data : data?.items || [])
        .map(norm)
        .filter((x) => x.id != null);
      setRows(arr);
    } catch (e) {
      console.error(e);
      toast.error("Errore caricamento fornitori");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function toggleSort(k) {
    if (sortBy === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(k);
      setSortDir("asc");
    }
  }

  const sorted = useMemo(() => {
    const dir = sortDir === "desc" ? -1 : 1;
    const get = (r) => {
      if (sortBy === "nome") {
        return `${r.nome || ""} ${r.cognome || ""}`.trim().toLowerCase();
      }
      return String(r[sortBy] || "").toLowerCase();
    };

    return [...rows].sort((a, b) =>
      get(a) < get(b) ? -1 * dir : get(a) > get(b) ? 1 * dir : 0,
    );
  }, [rows, sortBy, sortDir]);

  const SortHeader = ({ k, children, className = "" }) => (
    <button
      type="button"
      className={`af-thbtn ${className} ${sortBy === k ? "is-active" : ""}`}
      onClick={() => toggleSort(k)}
      aria-sort={
        sortBy === k ? (sortDir === "asc" ? "ascending" : "descending") : "none"
      }
      title={`Ordina per ${children}`}
    >
      <span>{children}</span>
      <span className="af-caret" aria-hidden>
        {sortBy === k ? (sortDir === "asc" ? "▲" : "▼") : "↕"}
      </span>
    </button>
  );

  // anagrafica
  const openEdit = async (id) => {
    setPhoto(null);
    setCoverUrl("");
    setOriginalUrl("");
    setCropping(false);
    setCrop(null);
    setEditLoading(true);
    setOpenId(id);
    try {
      const full = await getFornitoreCompleto(id, tkn);
      setCoverUrl(full?.cover_url || "");
      setOriginalUrl(full?.original_cover_url || full?.cover_url || "");
      setEdit({
        nome: full?.name ?? full?.ragione_sociale ?? "",
        cognome: full?.cognome ?? full?.last_name ?? full?.surname ?? "",
        alias: full?.nome ?? full?.slug ?? "",
        telefono: full?.telefono ?? "",
        email: full?.email ?? "",
        indirizzo: full?.indirizzo ?? "",
        citta: full?.citta ?? full?.city ?? "",
        cap: full?.cap ?? "",
        provincia: full?.provincia ?? "",
        codice_fiscale: full?.codice_fiscale ?? "",
        partita_iva: full?.partita_iva ?? "",
      });
    } catch {
      const b = rows.find((x) => x.id === id);
      setEdit({
        nome: b?.nome || "",
        cognome: b?.cognome || "",
        alias: b?.alias || "",
        telefono: b?.telefono || "",
        email: b?.email || "",
        indirizzo: "",
        citta: b?.citta || "",
        cap: "",
        provincia: b?.provincia || "",
        codice_fiscale: "",
        partita_iva: "",
      });
    } finally {
      setEditLoading(false);
    }
  };

  const closeEdit = () => {
    if (saving || editLoading) return;
    setOpenId(null);
    setPhoto(null);
  };

  // duplica
  const openDuplica = (row) => {
    setDupId(row.id);
    setDupNome(`${row.alias || row.nome || ""} (copia)`.trim());
  };

  const closeDuplica = () => {
    if (dupSaving) return;
    setDupId(null);
    setDupNome("");
  };

  const confirmDuplica = async () => {
    if (!String(dupNome).trim()) return toast.error("Nome obbligatorio");
    try {
      setDupSaving(true);
      await duplicateFornitore(dupId, { nome: dupNome.trim() }, tkn);
      toast.success("Fornitore duplicato");
      setDupId(null);
      setDupNome("");
      await load();
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Errore duplicazione fornitore");
    } finally {
      setDupSaving(false);
    }
  };

  const saveEdit = async () => {
    if (saving || editLoading) return;
    if ((photo || cropping) && !crop) return toast.error("Conferma il ritaglio della foto");
    if (!String(edit.nome).trim()) return toast.error("Nome obbligatorio");
    try {
      setSaving(true);
      const resp = await updateFornitore(openId, crop ? { ...edit, cover_crop: crop } : edit, photo);
      if (resp?.error) throw new Error(resp.error);
      toast.success("Anagrafica salvata");
      setCoverUrl(resp?.fornitore?.cover_url || coverUrl);
      setOriginalUrl(resp?.fornitore?.original_cover_url || originalUrl);
      setCrop(null);
      setCropping(false);
      setPhoto(null);
      if (!photo && !cropping) setOpenId(null);
      await load();
    } catch (e) {
      console.error(e);
      toast.error(e.name === "TimeoutError" || e.name === "AbortError"
        ? "Tempo di attesa scaduto. Ricarica la scheda per verificare il salvataggio."
        : e.message || "Errore salvataggio anagrafica");
    } finally {
      setSaving(false);
    }
  };

  const hasRows = sorted.length > 0;

  return (
    <div className="af-page">
      <header className="af-header">
        <h1>Fornitori</h1>
      </header>

      <main className="af-main">
        <div className="af-table" role="table" aria-label="Elenco fornitori">
          <div className="af-row af-row--head" role="row">
            <div className="af-cell" role="columnheader">
              <SortHeader k="nome">Nome</SortHeader>
            </div>
            <div className="af-cell" role="columnheader">
              <SortHeader k="alias">Alias</SortHeader>
            </div>
            <div className="af-cell" role="columnheader">
              <SortHeader k="telefono">Telefono</SortHeader>
            </div>
            <div className="af-cell" role="columnheader">
              <SortHeader k="email">Email</SortHeader>
            </div>
            <div className="af-cell" role="columnheader">
              <SortHeader k="citta">Città</SortHeader>
            </div>
            <div className="af-cell" role="columnheader">
              <SortHeader k="provincia">Provincia</SortHeader>
            </div>
            <div className="af-cell af-ta-center" role="columnheader">
              Azioni
            </div>
          </div>

          {loading &&
            Array.from({ length: 10 }).map((_, i) => (
              <div
                className="af-row af-skel"
                role="row"
                aria-busy="true"
                key={`sk-${i}`}
              >
                <div className="af-cell">.</div>
                <div className="af-cell">.</div>
                <div className="af-cell">.</div>
                <div className="af-cell">.</div>
                <div className="af-cell">.</div>
                <div className="af-cell af-ta-center">.</div>
              </div>
            ))}

          {!loading && !hasRows && (
            <div className="af-empty">Nessun fornitore trovato.</div>
          )}

          {!loading &&
            hasRows &&
            sorted.map((r) => (
              <div className="af-row" role="row" key={r.id}>
                <div
                  className="af-cell af-cell--name"
                  role="cell"
                  data-label="Nome"
                >
                  <div className="af-strong">
                    {[r.nome, r.cognome].filter(Boolean).join(" ") || "—"}
                  </div>
                </div>
                <div
                  className="af-cell af-cell--alias"
                  role="cell"
                  data-label="Alias"
                >
                  {r.alias || "—"}
                </div>
                <div
                  className="af-cell af-cell--tel"
                  role="cell"
                  data-label="Telefono"
                >
                  {r.telefono || "—"}
                </div>
                <div
                  className="af-cell af-cell--email"
                  role="cell"
                  data-label="Email"
                >
                  {r.email || "—"}
                </div>
                <div
                  className="af-cell af-cell--city"
                  role="cell"
                  data-label="Città"
                >
                  {r.citta || "—"}
                </div>
                <div
                  className="af-cell af-cell--provincia"
                  role="cell"
                  data-label="Provincia"
                >
                  {r.provincia || "—"}
                </div>
                <div
                  className="af-cell af-cell--actions"
                  role="cell"
                  data-label="Azioni"
                >
                  <button
                    className="af-btn af-iconbtn"
                    title="Anagrafica"
                    aria-label="Anagrafica"
                    onClick={() => openEdit(r.id)}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  </button>

                  <Link
                    className="af-btn af-iconbtn"
                    title="Listini / Orari / Logistica"
                    aria-label="Listini / Orari / Logistica"
                    to={`/admin/gestioneservizifornitore?supplier=${encodeURIComponent(
                      r.id,
                    )}`}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
                  </Link>

                  <Link
                    className="af-btn af-iconbtn"
                    title="Ordini"
                    aria-label="Ordini"
                    to={`/fornitore/ordini?fornitore=${encodeURIComponent(
                      r.id,
                    )}`}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                  </Link>

                  <button
                    className="af-btn af-iconbtn"
                    title="Duplica"
                    aria-label="Duplica"
                    onClick={() => openDuplica(r)}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                  </button>
                </div>
              </div>
            ))}
        </div>
      </main>

      {isOpen && (
        <div
          className="af-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Anagrafica fornitore"
          onClick={closeEdit}
        >
          <div className="af-modal" onClick={(e) => e.stopPropagation()}>
            <div className="af-modal-h">
              <div className="af-title">Anagrafica</div>
              <button
                className="af-close"
                onClick={closeEdit}
                aria-label="Chiudi"
              >
                ×
              </button>
            </div>

            <div className="af-form">
              <div className="af-field">
                <label htmlFor="supplier-photo">Foto del fornitore</label>
                {coverUrl ? (
                  <img src={coverUrl} alt="Foto attuale del fornitore"
                    style={{ width: 200, maxWidth: "100%", height: "auto", aspectRatio: SUPPLIER_IMAGE_CSS_RATIO, objectFit: "cover", borderRadius: 8 }} />
                ) : <span>Nessuna foto presente</span>}
                <input id="supplier-photo" type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                  disabled={saving || editLoading}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (!/^image\/(jpeg|png|webp|gif)$/.test(file.type) || file.size > 3 * 1024 * 1024) {
                      toast.error("Scegli un'immagine JPEG, PNG, WebP o GIF di massimo 3 MB");
                      e.target.value = "";
                      return;
                    }
                    setPhoto(file);
                    setCrop(null);
                    setCropping(true);
                  }} />
                <small>JPEG, PNG, WebP o GIF, massimo 3 MB.</small>
                {photo && <button type="button" className="af-btn" disabled={saving}
                  onClick={() => { setPhoto(null); setCrop(null); setCropping(false); document.getElementById("supplier-photo").value = ""; }}>
                  Annulla nuova foto
                </button>}
                {!photo && originalUrl && <button type="button" className="af-btn" disabled={saving || editLoading}
                  onClick={() => { setCropping(!cropping); setCrop(null); }}>
                  {cropping ? "Annulla ritaglio" : "Ritaglia foto esistente"}
                </button>}
                {cropping && (previewUrl || originalUrl) && <SupplierImageCropper
                  source={photo ? previewUrl : originalUrl} disabled={saving || editLoading}
                  onConfirm={setCrop} onChange={() => setCrop(null)} />}
              </div>
              <div className="af-2col af-identity-row">
                <div className="af-field">
                  <label>Nome *</label>
                  <input
                    value={edit.nome}
                    onChange={(e) =>
                      setEdit((s) => ({ ...s, nome: e.target.value }))
                    }
                  />
                </div>

                <div className="af-field">
                  <label>Cognome</label>
                  <input
                    value={edit.cognome}
                    onChange={(e) =>
                      setEdit((s) => ({ ...s, cognome: e.target.value }))
                    }
                  />
                </div>
                <div className="af-field">
                  <label>Codice fiscale</label>
                  <input
                    value={edit.codice_fiscale}
                    onChange={(e) =>
                      setEdit((s) => ({ ...s, codice_fiscale: e.target.value }))
                    }
                  />
                </div>
              </div>

              <div className="af-2col">
                <div className="af-field">
                  <label>Email</label>
                  <input
                    type="email"
                    value={edit.email}
                    onChange={(e) =>
                      setEdit((s) => ({ ...s, email: e.target.value }))
                    }
                  />
                </div>

                <div className="af-field">
                  <label>Telefono</label>
                  <input
                    value={edit.telefono}
                    onChange={(e) =>
                      setEdit((s) => ({ ...s, telefono: e.target.value }))
                    }
                  />
                </div>

              </div>
              <div className="af-field">
                <label>Via e numero civico</label>
                <input
                  value={edit.indirizzo}
                  onChange={(e) =>
                    setEdit((s) => ({ ...s, indirizzo: e.target.value }))
                  }
                />
              </div>

              <div className="af-2col af-location-row">
                <div className="af-field">
                  <CityField
                    label="Città"
                    value={edit.citta}
                    provinciaValue={edit.provincia}
                    capValue={edit.cap}
                    hardConfirm
                    confirmed={Boolean(edit.citta && edit.provincia)}
                    setConfirmed={() => {}}
                    resetProvinciaOnType={true}
                    resetCapOnType={true}
                    onChangeCity={(v, meta) => {
                      setEdit((s) => ({
                        ...s,
                        citta: v,
                        provincia: meta?.resetProvincia ? "" : s.provincia,
                        cap: meta?.resetCap ? "" : s.cap,
                      }));
                    }}
                    onApplyCity={({ name, provincia, cap }) => {
                      setEdit((s) => ({
                        ...s,
                        citta: name || "",
                        provincia: (provincia || "").toUpperCase(),
                        cap: cap || "",
                      }));
                    }}
                    showEmptyHint
                  />
                </div>

                <div className="af-field">
                  <label>Provincia</label>
                  <input value={edit.provincia} readOnly placeholder="es. MO" />
                </div>

                <div className="af-field">
                  <label>CAP</label>
                  <input
                    value={edit.cap}
                    inputMode="numeric"
                    onChange={(e) =>
                      setEdit((s) => ({
                        ...s,
                        cap: e.target.value.replace(/\D/g, "").slice(0, 5),
                      }))
                    }
                  />
                </div>
              </div>

              <div className="af-field">
                <label>Alias</label>
                <input
                  value={edit.alias}
                  onChange={(e) =>
                    setEdit((s) => ({ ...s, alias: e.target.value }))
                  }
                />
              </div>

              <div className="af-2col">
                <div className="af-field">
                  <label>Partita IVA</label>
                  <input
                    value={edit.partita_iva}
                    onChange={(e) =>
                      setEdit((s) => ({ ...s, partita_iva: e.target.value }))
                    }
                  />
                </div>
              </div>
            </div>

            <div className="af-modal-f">
              <button className="af-btn" onClick={closeEdit}>
                Annulla
              </button>
              <button className="af-btn af-btn--cta" onClick={saveEdit} disabled={saving || editLoading}>
                {saving ? "Salvataggio…" : "Salva"}
              </button>
            </div>
          </div>
        </div>
      )}

      {isDupOpen && (
        <div
          className="af-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Duplica fornitore"
          onClick={closeDuplica}
        >
          <div className="af-modal" onClick={(e) => e.stopPropagation()}>
            <div className="af-modal-h">
              <div className="af-title">Duplica fornitore</div>
              <button
                className="af-close"
                onClick={closeDuplica}
                aria-label="Chiudi"
              >
                ×
              </button>
            </div>

            <div className="af-form">
              <p style={{ margin: 0, color: "var(--af-muted)", fontSize: 12.5 }}>
                Verrà creato un nuovo fornitore con la stessa anagrafica
                fiscale, listino, orari, zone, chiusure e piani abbonamento
                dell'originale. Ordini, fatture e storico non vengono copiati.
              </p>
              <div className="af-field">
                <label>Nome nuovo alias *</label>
                <input
                  value={dupNome}
                  autoFocus
                  onChange={(e) => setDupNome(e.target.value)}
                />
              </div>
            </div>

            <div className="af-modal-f">
              <button
                className="af-btn"
                onClick={closeDuplica}
                disabled={dupSaving}
              >
                Annulla
              </button>
              <button
                className="af-btn af-btn--cta"
                onClick={confirmDuplica}
                disabled={dupSaving}
              >
                {dupSaving ? "Duplico…" : "Duplica"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
